/**
 * Presentational primitives for the Campaign Summary dashboard.
 *
 * The design constraint driving all of these: a 3px maximum corner radius, and
 * weight carried by hairline borders and typography rather than by shadow and
 * fill. PrimeReact's Lara theme rounds generously and pads generously, so the
 * panels here wrap its components and override both through `pt` rather than
 * fighting the theme stylesheet from a global CSS file.
 *
 * Status uses a tinted pill (see StatusCell). Tints are kept deliberately pale
 * — roughly a 5% wash rather than a saturated fill — so thirty stacked rows
 * still read as a calm table while each state stays instantly distinguishable.
 * Metric colour, by contrast, stays plain text: numbers are compared to each
 * other, and a filled cell would fight the pills for attention.
 */
import type { JSX, ReactNode } from 'react'
import { Panel } from 'primereact/panel'
import type { AgentStatus } from '@kc3/types'
import { useNow } from './clock-store.js'
import { formatDuration, formatDurationShort } from './format.js'

/** Semantic judgement → the three colour treatments used across the page. */
export type Band = 'good' | 'warn' | 'bad' | 'neutral'

const BAND_TEXT: Record<Band, string> = {
  good: 'text-emerald-600',
  warn: 'text-amber-600',
  bad: 'text-rose-600',
  neutral: 'text-slate-700',
}

const BAND_DOT: Record<Band, string> = {
  good: 'bg-emerald-500',
  warn: 'bg-amber-500',
  bad: 'bg-rose-500',
  neutral: 'bg-slate-400',
}

/**
 * A draggable dashboard panel.
 *
 * `.p-panel-header` is the grid's drag handle (see the page's
 * `draggableHandle`), so the header must stay a real PrimeReact panel header —
 * replacing it with a plain div would silently kill dragging.
 */
export function DashPanel({
  title,
  count,
  actions,
  children,
  /** Set for panels whose body is a table that scrolls internally. */
  flush = false,
}: {
  title: string
  count?: number | undefined
  actions?: ReactNode
  children: ReactNode
  flush?: boolean
}): JSX.Element {
  return (
    <Panel
      className="kc-panel h-full"
      header={
        <div className="flex items-center justify-between w-full gap-2 select-none">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 truncate">
              {title}
            </span>
            {count !== undefined && (
              <span className="shrink-0 rounded-[3px] bg-slate-100 px-1.5 py-px text-[10px] font-semibold tabular-nums text-slate-500">
                {count}
              </span>
            )}
          </div>
          {/* Stop drag-start on the action controls, or clicking a button in a
              panel header begins a grid drag instead of firing onClick. */}
          {actions && (
            <div
              className="flex items-center gap-1 shrink-0"
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              {actions}
            </div>
          )}
        </div>
      }
      pt={{
        root: { className: 'h-full flex flex-col rounded-[3px] border border-slate-200 bg-white shadow-none overflow-hidden' },
        header: { className: 'px-3 py-2 bg-slate-50/80 border-b border-slate-200 rounded-none cursor-move' },
        toggleableContent: { className: 'flex-1 min-h-0 flex flex-col' },
        content: {
          className: `flex-1 min-h-0 overflow-hidden border-0 rounded-none ${flush ? 'p-0' : 'p-3'}`,
        },
      }}
    >
      {children}
    </Panel>
  )
}

/**
 * A single KPI figure.
 *
 * `tabular-nums` matters more than it looks: these values update every second
 * or two, and proportional digits make the whole tile jitter horizontally on
 * each change.
 */
export function StatTile({
  label,
  value,
  sub,
  band = 'neutral',
}: {
  label: string
  /** ReactNode so a tile can host a self-updating <Elapsed>. */
  value: ReactNode
  sub?: ReactNode
  band?: Band
}): JSX.Element {
  // Same card grammar as the My Dashboard tiles: state rides on the label
  // dot (band → colour), the value stays neutral ink in the mono font, and
  // context sits on the hint line beneath it.
  return (
    <div className="kc-tile">
      <div className="flex min-w-0 items-center gap-1.5">
        <span className={`kc-dot inline-block h-2 w-2 shrink-0 ${BAND_DOT[band]}`} />
        <span className="kc-label truncate">{label}</span>
      </div>
      <div className="kc-tile-value mt-1 truncate font-mono font-extrabold tracking-tight">{value}</div>
      {sub && <div className="kc-tile-sub truncate">{sub}</div>}
    </div>
  )
}

/**
 * Coloured dot + label, used for every status/state cell on the page.
 *
 * `kc-dot` exempts the dot from the page's 3px radius ceiling — it is meant to
 * be a circle, and the blanket rule in dashboard.css would otherwise square it
 * into a tiny rounded box.
 */
export function Dot({ band, label }: { band: Band; label: string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 min-w-0">
      <span className={`kc-dot inline-block w-1.5 h-1.5 shrink-0 ${BAND_DOT[band]}`} />
      <span className="truncate text-[12px] text-slate-700">{label}</span>
    </span>
  )
}

/**
 * Agent status → visual tone + label.
 *
 * SIX tones, not three. The previous mapping collapsed Ringing, Paused,
 * On Break, Wrap-up, Reserving, Reserved and Inviting all onto one amber
 * "warn", and rendered On Call as neutral grey — so the single most important
 * state on the floor was the least visible, and five operationally different
 * states were indistinguishable at a glance. A supervisor scanning this column
 * needs "ready", "working", "mid-transition", "wrapping up" and "away" to look
 * different, because each one implies a different action.
 *
 * The map is exhaustive over `AgentStatus`: adding a status to the union
 * surfaces as a compile error here rather than silently rendering grey.
 */
export type StatusTone =
  | 'ready'
  | 'active'
  | 'transition'
  | 'wrapup'
  | 'away'
  | 'offline'

const STATUS_META: Record<AgentStatus, { tone: StatusTone; label: string }> = {
  // IDLE means "in the available set, waiting for a call" — the operator's
  // chosen wallboard wording is 'Waiting (Available)': the agent's toggle is
  // ON (Ready in the agent UI) and the dialer may route to them. 'Idle' alone
  // reads as "doing nothing" — its meaning in the agent UI's Ready/Idle
  // toggle, where it means NOT ready — so the parenthetical carries the
  // availability signal.
  IDLE: { tone: 'ready', label: 'Waiting (Available)' },
  AVAILABLE_ON_DEMAND: { tone: 'ready', label: 'Waiting (Available)' },
  IN_CALL: { tone: 'active', label: 'On Call' },
  ANSWERED: { tone: 'active', label: 'Answered' },
  RINGING: { tone: 'transition', label: 'Ringing' },
  RESERVING: { tone: 'transition', label: 'Reserving' },
  RESERVED: { tone: 'transition', label: 'Reserved' },
  INVITING: { tone: 'transition', label: 'Inviting' },
  ACW: { tone: 'wrapup', label: 'Wrap-up' },
  // Toggle OFF — 'Idle (Available)' per the operator's wording: the agent is
  // still logged in and present, just not taking routed calls.
  PAUSED: { tone: 'away', label: 'Idle (Available)' },
  ON_BREAK: { tone: 'away', label: 'On Break' },
  REGISTERED: { tone: 'away', label: 'Registered' },
  OFFLINE: { tone: 'offline', label: 'Offline' },
}

export function statusMeta(status: AgentStatus): { tone: StatusTone; label: string } {
  return STATUS_META[status] ?? { tone: 'offline', label: String(status) }
}

/**
 * A status badge: tinted pill + dot.
 *
 * A pill rather than bare text because this column is scanned vertically down
 * thirty rows — a filled shape gives the eye an edge to follow that plain
 * coloured text does not. `pi-spin` on the transitional states makes "something
 * is happening right now" legible without another column.
 */
export function StatusCell({
  status,
  reason,
}: {
  status: AgentStatus
  /**
   * The pause/break reason the agent entered the status with (the break-code
   * text, e.g. "Lunch"). 'Idle' is suppressed — it is the Ready/Idle toggle's
   * generic pause marker, not a break code, and renders as
   * "Idle (Available) · Idle", which reads as a bug.
   */
  reason?: string
}): JSX.Element {
  const meta = statusMeta(status)
  const live = meta.tone === 'active' || meta.tone === 'transition'
  const shownReason = reason && reason !== 'Idle' ? reason : undefined
  return (
    <span className={`kc-status kc-status-${meta.tone}`}>
      <span className={`kc-dot kc-status-dot${live ? ' kc-status-dot-live' : ''}`} />
      {meta.label}
      {shownReason ? ` · ${shownReason}` : ''}
    </span>
  )
}


/** Right-aligned numeric cell — every metric column in every table uses it. */
export function Num({
  children,
  band = 'neutral',
  muted = false,
}: {
  children: ReactNode
  band?: Band
  muted?: boolean
}): JSX.Element {
  return (
    <span
      className={`block text-right tabular-nums text-[12px] ${
        muted ? 'text-slate-400' : BAND_TEXT[band]
      }`}
    >
      {children}
    </span>
  )
}

/** Live/stale indicator for the toolbar. */
export function LiveBadge({ live }: { live: boolean }): JSX.Element {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-[3px] border px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] ${
        live
          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
          : 'border-slate-200 bg-slate-50 text-slate-500'
      }`}
    >
      <span
        className={`kc-pulse inline-block w-1.5 h-1.5 ${
          live ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'
        }`}
      />
      {live ? 'Live' : 'Offline'}
    </span>
  )
}

/**
 * Shared PrimeReact DataTable `pt` overrides.
 *
 * Spread into every table so density, borders and the sticky header behave
 * identically across all nine of them.
 */
export const TABLE_PT = {
  root: { className: 'text-[12px]' },
  thead: { className: 'sticky top-0 z-10' },
  headerRow: { className: '' },
  bodyRow: { className: 'hover:bg-slate-50/80' },
} as const

/** Column header/body classes reused by every table. */
export const TH = 'bg-slate-50 text-[10px] font-semibold uppercase tracking-[0.07em] text-slate-500 border-slate-200'
export const TD = 'text-[12px] text-slate-700 border-slate-100'

/* ── Richer table cells ─────────────────────────────────────────────────── */

/**
 * Initials monogram for an agent row.
 *
 * Colour is derived from the NAME, not from status or row index, so a given
 * person keeps the same chip everywhere on the dashboard and across reloads —
 * that stability is what lets the eye use it as an identifier rather than
 * decoration. A row index would reshuffle every time the table re-sorted.
 *
 * The palette is a fixed set of muted pairs rather than a computed HSL, so no
 * combination can come out illegible against the row background.
 */
const AVATAR_TONES: ReadonlyArray<{ bg: string; fg: string }> = [
  { bg: '#eef2ff', fg: '#4338ca' },
  { bg: '#ecfdf5', fg: '#047857' },
  { bg: '#fef3c7', fg: '#b45309' },
  { bg: '#fae8ff', fg: '#a21caf' },
  { bg: '#e0f2fe', fg: '#0369a1' },
  { bg: '#ffe4e6', fg: '#be123c' },
  { bg: '#f1f5f9', fg: '#475569' },
  { bg: '#f0fdfa', fg: '#0f766e' },
]

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return (parts[0] as string).slice(0, 2).toUpperCase()
  return ((parts[0] as string)[0]! + (parts[parts.length - 1] as string)[0]!).toUpperCase()
}

function toneFor(name: string): { bg: string; fg: string } {
  let h = 0
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0
  return AVATAR_TONES[h % AVATAR_TONES.length]!
}

export function Avatar({ name }: { name: string | null | undefined }): JSX.Element {
  // Tolerate null/undefined: the wire types say non-null, but a single bad row
  // (e.g. a call whose agent record was deleted) used to throw here and take
  // the entire dashboard down through the error boundary.
  const safe = name ?? ''
  const tone = toneFor(safe)
  return (
    <span
      className="kc-avatar"
      style={{ background: tone.bg, color: tone.fg }}
      aria-hidden="true"
    >
      {initials(safe)}
    </span>
  )
}

/**
 * A count with a proportional bar beneath it.
 *
 * The bar is scaled to the largest value in the SAME column, which turns a
 * column of numbers into a ranked shape you can read without comparing digits.
 * Absolute scaling would make every bar in a quiet window invisible.
 */
export function BarNum({
  value,
  max,
  format,
  band = 'neutral',
}: {
  value: number
  max: number
  format: (n: number) => string
  band?: Band
}): JSX.Element {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0
  return (
    <span className="kc-barnum">
      <span className={`kc-barnum-value ${BAND_TEXT[band]}`}>{format(value)}</span>
      <span className="kc-barnum-track">
        <span
          className={`kc-barnum-fill kc-fill-${band}`}
          style={{ width: `${pct}%` }}
        />
      </span>
    </span>
  )
}

/**
 * A percentage as a compact meter plus its value.
 *
 * Used for the two figures a supervisor judges rather than reads — service
 * level and abandon rate. The meter always spans 0-100 so the fill position
 * means the same thing in every row, unlike the column-relative bars above.
 */
export function MeterPct({ pct, band }: { pct: number; band: Band }): JSX.Element {
  const clamped = Math.max(0, Math.min(100, Number.isFinite(pct) ? pct : 0))
  return (
    <span className="kc-meterpct">
      <span className={`kc-meterpct-value ${BAND_TEXT[band]}`}>
        {Math.round(clamped * 10) / 10}%
      </span>
      <span className="kc-meterpct-track">
        <span className={`kc-meterpct-fill kc-fill-${band}`} style={{ width: `${clamped}%` }} />
      </span>
    </span>
  )
}

/** Leaderboard position. Only the top three earn a medal tint. */
export function Rank({ index }: { index: number }): JSX.Element {
  const n = index + 1
  const cls = n <= 3 ? `kc-rank kc-rank-${n}` : 'kc-rank'
  return <span className={cls}>{n}</span>
}

/* ── Self-updating durations ────────────────────────────────────────────── */

/**
 * A duration that counts up on its own.
 *
 * This is the component that makes the dashboard both live AND smooth. It
 * subscribes to the shared clock directly, so a tick re-renders THIS SPAN and
 * nothing else — the table row, the table and the grid around it are never
 * touched. Previously the clock was part of the live-data snapshot, so moving
 * one `0:42` to `0:43` re-rendered every table on the page.
 *
 * `from` is an ISO instant (a call's `startedAt`, an agent's `statusSince`);
 * the elapsed value is derived, never stored, so it cannot drift.
 */
export function Elapsed({
  from,
  variant = 'full',
  band = 'neutral',
  bandAfterSec,
  bandTo = 'warn',
  className = '',
}: {
  from: string | undefined
  /** `full` → 1:02:03 · `short` → 1h 02m / 4m 12s */
  variant?: 'full' | 'short'
  band?: Band
  /** Escalate to `bandTo` once the duration passes this many seconds. */
  bandAfterSec?: number
  bandTo?: Band
  className?: string
}): JSX.Element {
  const now = useNow()
  if (!from) return <span className={`kc-elapsed ${className}`}>—</span>

  const started = new Date(from).getTime()
  const sec = Number.isNaN(started) ? 0 : Math.max(0, Math.floor((now - started) / 1000))
  const text = variant === 'short' ? formatDurationShort(sec) : formatDuration(sec)
  const tone =
    bandAfterSec !== undefined && sec >= bandAfterSec ? bandTo : band

  return (
    <span className={`kc-elapsed ${BAND_TEXT[tone]} ${className}`}>{text}</span>
  )
}

/**
 * The live pill shown against a call in progress: pulsing dot + running timer.
 * Same self-updating property as `Elapsed` — only this span repaints.
 */
export function LiveCallTimer({ from }: { from: string }): JSX.Element {
  return (
    <span className="kc-livetimer">
      <span className="kc-dot kc-livetimer-dot" />
      <Elapsed from={from} />
    </span>
  )
}

/**
 * Countdown to a wrap-up (ACW) deadline.
 *
 * `until` is the ISO8601 instant the wrap-up timer expires, sourced from
 * the Redis `wrap_up_until` key via the supervisor snapshot. The remaining
 * time is derived on each tick of `useNow`, so this span self-updates
 * without re-rendering its parent — same property as `Elapsed`.
 *
 * Renders `—` when `until` is absent (agent not in wrap-up, or no timer
 * running). Clamps at 0 when the deadline has passed so the cell never
 * shows a negative duration.
 */
export function WrapUpCountdown({ until }: { until: string | undefined }): JSX.Element {
  const now = useNow()
  if (!until) return <span className="kc-elapsed kc-band-neutral">—</span>

  const deadline = new Date(until).getTime()
  if (Number.isNaN(deadline)) return <span className="kc-elapsed kc-band-neutral">—</span>

  const remainingMs = deadline - now
  const sec = remainingMs <= 0 ? 0 : Math.floor(remainingMs / 1000)
  // Escalate tone as the deadline approaches: under 10s reads as 'bad',
  // under 30s as 'warn', otherwise neutral. Mirrors the banding the
  // Elapsed component applies to long-idle states.
  const tone = sec <= 0 ? 'bad' : sec < 10 ? 'bad' : sec < 30 ? 'warn' : 'neutral'

  return <span className={`kc-elapsed ${BAND_TEXT[tone]}`}>{formatDuration(sec)}</span>
}
